import ViewerFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragmentConfig";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import TitledTextFieldViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/TitledTextFieldViewerFragment/TitledTextFieldViewerFragment";
import CompletableChecklistViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/CompletableChecklistViewerFragment/CompletableChecklistViewerFragment";
import UserNameViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserNameViewerFragment/UserNameViewerFragment";
import TitledBoxedTextViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/TitledBoxedTextViewerFragment/TitledBoxedTextViewerFragment";
import UserAndTeamViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserAndTeamViewerFragment/UserAndTeamViewerFragment";
import LocationViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/LocationViewerFragment/LocationViewerFragment";
import InvalidViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/InvalidViewerFragment/InvalidViewerFragment";
import { Loading } from "@coworker/apprestructured/src/task-management/loading";

export type ViewerFragmentCommonProps = {
  // Block all user interaction with this fragment, usually while making api requests.
  blockInput: boolean;
  // The task being viewed, which is the original task merged with all pending changes.
  task: Record<string, unknown> | Loading;
  // Callback for making changes to the task that is being viewed.
  // Note that this doesn't trigger any api request. It just adds a part to the PatchTask
  // that is sent when the user explicitly saves the task later on.
  onTaskChange: (part: Record<string, unknown>) => void;
};

type ViewerFragmentProps = {
  config: ViewerFragmentConfig;
  commonProps: ViewerFragmentCommonProps;
};

function ViewerFragment({ config, commonProps }: ViewerFragmentProps) {
  const { id, props: configProps } = config;
  switch (id) {
    case "titled-boxed-text-viewer-fragment": {
      return (
        <TitledBoxedTextViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "titled-text-field-viewer-fragment": {
      return (
        <TitledTextFieldViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "completable-checklist-viewer-fragment": {
      return (
        <CompletableChecklistViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "user-name-viewer-fragment": {
      return (
        <UserNameViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "user-and-team-viewer-fragment": {
      return (
        <UserAndTeamViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "location-viewer-fragment": {
      return (
        <LocationViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    default: {
      console.error(`'${id}' does not map to any viewer fragment component`);
      reportMessageToSentry(
        `Viewer fragment config id does not map to any viewer fragment component`,
        {
          id,
        },
        "error"
      );
      return <InvalidViewerFragment />;
    }
  }
}

export default ViewerFragment;
